import { OfferNetworkProvider } from "@redotech/crdb-enums/marketing/offer-network-provider";
import { ResolvedOfferConfig } from "@redotech/order-tracking-common/offer-network/offer-placement";
import { OfferNetworkAttributes, toUptickAttributes } from "./offer-attributes";
import { OfferPlacementHandle } from "./offer-placement";
import { loadScriptOnce, unloadScript, waitForGlobal } from "./script-loader";

/** Per-place bundle; the place id is a path segment, not a parameter. */
const sdkUrl = (placeId: string) =>
  `https://app.uptick.com/i/places/${placeId}/offers.js`;

/** Events the init callback reports; we only act on the terminal one. */
type UptickFlowEvent = { type?: string } | string;

type UptickSdk = (
  method: "init",
  target: HTMLElement,
  config: Record<string, string>,
  callback?: (event: UptickFlowEvent) => void,
) => void;

declare global {
  interface Window {
    uptick?: UptickSdk;
  }
}

/**
 * Uptick documents no unmount, so a place's script and global are torn down
 * once nothing is mounted against them — the same reset the Falcon adapter
 * needs, and what lets an SPA remount start a fresh flow instead of reusing a
 * completed one.
 */
const mountsByPlace = new Map<string, number>();

function releaseMount(placeId: string): void {
  const remaining = (mountsByPlace.get(placeId) ?? 1) - 1;
  if (remaining > 0) {
    mountsByPlace.set(placeId, remaining);
    return;
  }
  mountsByPlace.delete(placeId);
  if (mountsByPlace.size === 0) {
    delete window.uptick;
    removeResetCounter();
  }
  unloadScript(sdkUrl(placeId));
}

/**
 * Uptick appends a per-place stylesheet to document.head, and it carries an
 * unscoped Tailwind form reset — `[type=text],select,textarea { border-width:1px;
 * border-radius:0; background-color:#fff; padding:.5rem .75rem; font-size:1rem }`
 * and friends. Our inputs declare none of those (the wrapper owns the border),
 * so their rule wins and every field on the host page grows a square inner box.
 * On a tracking page that is the merchant's own UI, not just ours.
 *
 * This undoes it for anything outside an Uptick container. `:not(.uptick *)`
 * carries (0,1,0), so with the element list the counter is (0,1,1) and beats
 * their `[type=text]` regardless of injection order.
 *
 * STOPGAP. The real fix is Uptick scoping their reset to `.uptick`; this exists
 * so a third-party stylesheet cannot restyle merchant pages in the meantime.
 */
const RESET_COUNTER_ID = "redo-uptick-reset-counter";
const RESET_COUNTER_CSS = `
:is(input, select, textarea):not(.uptick *) {
  background-color: transparent;
  border-radius: 0;
  border-width: 0;
  font-size: inherit;
  line-height: inherit;
  padding: 0;
}
:is(input, textarea):not(.uptick *)::placeholder {
  color: inherit;
  opacity: inherit;
}
`;

function ensureResetCounter(): void {
  if (document.getElementById(RESET_COUNTER_ID)) {
    return;
  }
  const style = document.createElement("style");
  style.id = RESET_COUNTER_ID;
  style.textContent = RESET_COUNTER_CSS;
  document.head.append(style);
}

function removeResetCounter(): void {
  document.getElementById(RESET_COUNTER_ID)?.remove();
}

/**
 * Config for `uptick("init", ...)`. Deliberately omits `placement`: a web place
 * is configured with exactly one, and naming it makes Uptick look up a
 * placement that does not exist there ("Integration placement not found", 422).
 * Omitting it resolves the place's own placement. The Shopify channel is the
 * opposite — its place holds several and the param selects between them — which
 * is why only that channel sends one.
 *
 * `app_id` and `site_id` are read on the offer request rather than the flow
 * request, so they are absent from the first call's query string by design.
 */
export function uptickInitConfig(
  config: Extract<
    ResolvedOfferConfig,
    { provider: OfferNetworkProvider.Uptick }
  >,
  attributes: OfferNetworkAttributes,
): Record<string, string> {
  return {
    ...toUptickAttributes(attributes),
    app_id: config.appExternalId,
    ...(config.siteId ? { site_id: config.siteId } : {}),
  };
}

export async function mountUptickPlacement(
  config: Extract<
    ResolvedOfferConfig,
    { provider: OfferNetworkProvider.Uptick }
  >,
  attributes: OfferNetworkAttributes,
  container: HTMLElement,
): Promise<OfferPlacementHandle> {
  const url = sdkUrl(config.placeId);
  await loadScriptOnce(url);
  const uptick = await waitForGlobal(() => window.uptick);

  uptick("init", container, uptickInitConfig(config, attributes));
  ensureResetCounter();
  mountsByPlace.set(
    config.placeId,
    (mountsByPlace.get(config.placeId) ?? 0) + 1,
  );

  let released = false;
  return {
    dispose: () => {
      if (released) {
        return;
      }
      released = true;
      container.replaceChildren();
      releaseMount(config.placeId);
    },
  };
}
